import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
    useGetPatientPrescriptionQuery,
    useUpdatePatientPrescriptionMutation,
} from "store";
import {
    Box,
    Spinner,
    SimpleGrid,
    Card,
    CardBody,
    CardHeader,
    FormControl,
    FormLabel,
    FormErrorMessage,
    Checkbox,
    Button,
    Text,
    Textarea,
    Grid,
    GridItem,
    Link as ChakraLink,
} from "@chakra-ui/react";

const PrescriptionUpdate = () => {
    const { patientId, presId } = useParams();
    console.log("Params:", { patientId, presId });
    const navigate = useNavigate();

    // Fetch the existing prescription data
    const {
        data: prescription,
        error,
        isLoading,
    } = useGetPatientPrescriptionQuery({
        patientId,
        presId,
    });

    // Mutation hook for updating the prescription
    const [
        updatePatientPrescription,
        { isLoading: isUpdating, error: updateError },
    ] = useUpdatePatientPrescriptionMutation();

    // Initialize the form with useForm
    const {
        register,
        handleSubmit,
        setValue,
        reset,
        formState: { errors },
    } = useForm();

    React.useEffect(() => {
        setValue("patient", patientId);
    }, [patientId, setValue]);

    const capitalizeFirstLetter = (str) => {
        if (!str) return str; // Handle empty string case
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    // Populate the form with existing prescription data
    useEffect(() => {
        if (prescription) {
            reset({
                repeat: prescription.repeat,
                notes: prescription.notes,
            });
        }
    }, [prescription, reset]);

    // Handle form submission
    const onSubmit = async (data) => {
        try {
            await updatePatientPrescription({
                patientId,
                presId,
                ...data,
            }).unwrap();
            navigate(`/patients/${patientId}/prescriptions`);
        } catch (error) {
            console.error("Error updating prescription:", error);
        }
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <>
            <Box mt={20} p={4}>
                <Card
                    boxShadow="md"
                    borderRadius="2xl"
                    overflow="hidden"
                    p={4}
                    border="1px solid"
                    borderColor="gray.300"
                >
                    <CardHeader borderBottom="1px solid" borderColor="blue.200">
                        <Box textAlign="center" mb={4}>
                            <Text fontSize="2xl" fontWeight="bold">
                                Update Prescription
                            </Text>
                        </Box>
                    </CardHeader>

                    <CardBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            {/* Repeat */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl>
                                        <FormLabel>Repeat</FormLabel>
                                        <Checkbox {...register("repeat")} />
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            {/* Notes */}
                            <SimpleGrid columns={{ base: 1, md: 1 }} mb={6}>
                                <Box>
                                    <FormControl isInvalid={errors.notes}>
                                        <FormLabel>Notes</FormLabel>
                                        <Textarea
                                            {...register("notes", {
                                                minLength: {
                                                    value: 10,
                                                    message:
                                                        "Notes should be at least 10 characters long",
                                                },
                                                maxLength: {
                                                    value: 500,
                                                    message:
                                                        "Notes should not exceed 500 characters",
                                                },
                                                onChange: (e) => {
                                                    const capitalizedValue = capitalizeFirstLetter(
                                                        e.target.value
                                                    );
                                                    setValue(
                                                        "notes",
                                                        capitalizedValue,
                                                        { shouldValidate: true }
                                                    );
                                                },
                                            })}
                                        />
                                        <FormErrorMessage>
                                            {errors.notes &&
                                                errors.notes.message}
                                        </FormErrorMessage>
                                    </FormControl>
                                </Box>
                            </SimpleGrid>

                            <Grid
                                templateColumns="repeat(2, 1fr)"
                                gap={4}
                                width="100%"
                            >
                                <GridItem>
                                    <ChakraLink
                                        as={Link}
                                        to={`/patients/${patientId}/prescriptions/${presId}/details`}
                                        color="blue.500"
                                    >
                                        Back to Details
                                    </ChakraLink>
                                </GridItem>
                                <GridItem textAlign="right">
                                    <Button
                                        colorScheme="blue"
                                        isLoading={isUpdating}
                                        loadingText="Loading..."
                                        spinner={<Spinner />}
                                        type="submit"
                                    >
                                        Update
                                    </Button>
                                </GridItem>
                            </Grid>

                            {updateError && (
                                <div>Error: {updateError.message}</div>
                            )}
                        </form>
                    </CardBody>
                </Card>
            </Box>
        </>
    );
};

export default PrescriptionUpdate;
